import { useEffect, useState } from 'react';
import { Link, NavLink, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { claims, logout } from './auth.js';
import { Home } from './pages/Home.js';
import { Add } from './pages/Add.js';
import { Expenses } from './pages/Expenses.js';
import { ExpenseDetail } from './pages/ExpenseDetail.js';
import { Imports } from './pages/Imports.js';
import { ImportReview } from './pages/ImportReview.js';
import { Wizard } from './pages/Wizard.js';
import { Withdrawals } from './pages/Withdrawals.js';
import { Packets } from './pages/Packets.js';
import { Settings } from './pages/Settings.js';
import { Mileage } from './pages/Mileage.js';
import { Guide } from './pages/Guide.js';
import { Trends } from './pages/Trends.js';
import { Icon, Mark, PageHead, Toaster } from './ui.js';
import { HelpBubble } from './help.js';

/**
 * The shell.
 *
 * Three places are used every week — Home, Add, Expenses — and they are the
 * only three in the navigation on a phone. Everything visited a few times a
 * year (imports, withdrawals, the audit packet, settings, help) lives in one
 * menu, so it costs no space on the screen where a receipt gets photographed.
 *
 * Trends is read at a desk, so it sits in the top bar where there is room for
 * it and in the menu on a phone, where the bottom bar stays at three.
 *
 * On a phone the three sit in a bottom bar with Add in the centre (goal #1:
 * capture in under 60 seconds). From 760px up they move into the top bar, and
 * the help bubble takes the bottom corner the bar left free.
 */
export function App() {
  const user = claims();
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  // Any navigation closes the menu, including the back button.
  useEffect(() => setMenuOpen(false), [location.pathname]);

  const who = user?.email ?? user?.['cognito:username'] ?? '';
  const initial = (who || '?').slice(0, 1).toUpperCase();
  // The review screen puts the receipt beside its fields and wants the room;
  // so do the charts.
  const wide = /^\/(expenses\/|imports\/|trends$)/.test(location.pathname);

  return (
    <div className="app">
      <header className="topbar">
        <Link className="brand" to="/">
          <Mark />
          Shoebox
        </Link>
        <nav className="topnav" aria-label="Main">
          <NavLink to="/" end className={active}>
            Home
          </NavLink>
          <NavLink to="/expenses" className={active}>
            Expenses
          </NavLink>
          <NavLink to="/trends" className={active}>
            Trends
          </NavLink>
        </nav>
        <span className="spacer" />
        <Link className="btn primary add-desktop" to="/add">
          <Icon name="plus" />
          Add
        </Link>
        <button
          className="avatar"
          aria-label="Menu"
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen((open) => !open)}
        >
          {initial}
        </button>
      </header>

      {menuOpen ? (
        <>
          <div className="menu-scrim" onClick={() => setMenuOpen(false)} />
          <nav className="menu" aria-label="More">
            {who ? <div className="menu-who">Signed in as {who}</div> : null}
            {/* In the top bar on a computer; here on a phone. */}
            <NavLink to="/trends" className={({ isActive }) => `phone-only${isActive ? ' active' : ''}`}>
              <Icon name="chart" />
              Trends
            </NavLink>
            <NavLink to="/imports" className={active}>
              <Icon name="box" />
              Imports
            </NavLink>
            <NavLink to="/withdrawals" className={active}>
              <Icon name="bank" />
              Withdrawals
            </NavLink>
            <NavLink to="/packet" className={active}>
              <Icon name="doc" />
              Audit packet
            </NavLink>
            <NavLink to="/settings" className={active}>
              <Icon name="people" />
              Family &amp; settings
            </NavLink>
            <NavLink to="/help" className={active}>
              <Icon name="help" />
              Help
            </NavLink>
            <hr />
            <button className="menu-item" onClick={logout}>
              <Icon name="out" />
              Sign out
            </button>
          </nav>
        </>
      ) : null}

      <main className={wide ? 'wide' : undefined}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/add" element={<Add />} />
          <Route path="/mileage" element={<Mileage />} />
          <Route path="/expenses" element={<Expenses />} />
          <Route path="/expenses/:id" element={<ExpenseDetail />} />
          <Route path="/trends" element={<Trends />} />
          <Route path="/imports" element={<Imports />} />
          <Route path="/imports/:id" element={<ImportReview />} />
          <Route path="/wizards/:channel" element={<Wizard />} />
          <Route path="/withdrawals" element={<Withdrawals />} />
          <Route path="/packet" element={<Packets />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/help" element={<Guide />} />

          {/* Addresses from before the simplification. Bookmarks, the user
              guide, and links in old emails keep working. */}
          <Route path="/capture" element={<Navigate to="/add" replace />} />
          <Route path="/review" element={<Navigate to="/expenses?status=draft" replace />} />
          <Route path="/distributions" element={<Navigate to="/withdrawals" replace />} />
          <Route path="/packets" element={<Navigate to="/packet" replace />} />
          <Route path="/guide" element={<Navigate to="/help" replace />} />

          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>

      <nav className="tabbar" aria-label="Main">
        <NavLink to="/" end className={active}>
          <Icon name="home" />
          Home
        </NavLink>
        <NavLink to="/add" className={({ isActive }) => `add${isActive ? ' active' : ''}`}>
          <span className="fab">
            <Icon name="plus" />
          </span>
          Add
        </NavLink>
        <NavLink to="/expenses" className={active}>
          <Icon name="list" />
          Expenses
        </NavLink>
      </nav>

      <HelpBubble />
      <Toaster />
    </div>
  );
}

function active({ isActive }: { isActive: boolean }): string {
  return isActive ? 'active' : '';
}

function NotFound() {
  return (
    <>
      <PageHead title="Page not found">That address does not lead anywhere.</PageHead>
      <div className="actions">
        <Link className="btn primary" to="/">
          Go to Home
        </Link>
      </div>
    </>
  );
}
